"use client";

import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { QueryClientProvider, QueryClient } from '@tanstack/react-query';
import { useState, useEffect } from 'react';
import { BrowserRouter, Route, Routes, Outlet, Navigate } from 'react-router-dom';
import { useCompetitionContext } from "../providers/CompetitionContext";
import { useTeamContext, TeamProvider } from '../providers/TeamContext';
import { RouteGuard } from './pages/RouteGuard';
import EditTeam from './pages/EditTeam';
import NewTeam from './pages/NewTeam';
import Points from './pages/Points';
import Leagues from './pages/Leagues';
// import Calendar from './pages/Calendar';
import Ranking from './pages/Ranking';
import { CircleChevronLeft, CircleChevronRight } from 'lucide-react';
import { Match, Week } from '@repo/types';

const LoadingSpinner = ({ size = 24, className = '' }) => {
  return (
    <div className="grid min-h-[140px] w-full place-items-center overflow-x-scroll rounded-lg p-6 lg:overflow-visible">
      <svg
        className={`text-gray-300 animate-spin ${className}`}
        viewBox="0 0 64 64"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        width={size}
        height={size}
      >
        <path
          d="M32 3C35.8083 3 39.5794 3.75011 43.0978 5.20749C46.6163 6.66488 49.8132 8.80101 52.5061 11.4939C55.199 14.1868 57.3351 17.3837 58.7925 20.9022C60.2499 24.4206 61 28.1917 61 32C61 35.8083 60.2499 39.5794 58.7925 43.0978C57.3351 46.6163 55.199 49.8132 52.5061 52.5061C49.8132 55.199 46.6163 57.3351 43.0978 58.7925C39.5794 60.2499 35.8083 61 32 61C28.1917 61 24.4206 60.2499 20.9022 58.7925C17.3837 57.3351 14.1868 55.199 11.4939 52.5061C8.801 49.8132 6.66487 46.6163 5.20749 43.0978C3.7501 39.5794 3 35.8083 3 32C3 28.1917 3.75011 24.4206 5.2075 20.9022C6.66489 17.3837 8.80101 14.1868 11.4939 11.4939C14.1868 8.80099 17.3838 6.66487 20.9022 5.20749C24.4206 3.7501 28.1917 3 32 3L32 3Z"
          stroke="currentColor"
          strokeWidth="5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M32 3C36.5778 3 41.0906 4.08374 45.1692 6.16256C49.2477 8.24138 52.7762 11.2562 55.466 14.9605C58.1558 18.6647 59.9304 22.9531 60.6448 27.4748C61.3591 31.9965 60.9928 36.6232 59.5759 40.9762"
          stroke="currentColor"
          strokeWidth="5"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="text-gray-900"
        />
      </svg>
    </div>
  );
};

import moment from 'moment';
import 'moment/locale/nl';

interface WeekSwitchProps {
  competitionData: any;
}

const WeekSwitch: React.FC<WeekSwitchProps> = ({ competitionData }) => {
  const { brand, weekId, setWeekId } = useCompetitionContext();
  const { team, updateForWeek } = useTeamContext();

  const weeks = competitionData.weeks || [];
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const currentPath = location.pathname;

  if (weeks.length === 0) {
    return <div>No weeks available</div>;
  }

  const isRootRoute = currentPath === ('/fantasy') || currentPath === ('/fantasy/');
  const isNewTeamRoute = currentPath.startsWith('/fantasy/new')
  const isEditRoute = currentPath.startsWith('/fantasy/edit/');
  const isPointsRoute = currentPath.startsWith('/fantasy/points/') || currentPath.startsWith('/fantasy/public/');
  const isPublicRoute = currentPath.startsWith('/fantasy/public/');
  const showWeekSwitch = !currentPath.startsWith('/fantasy/leagues') && !currentPath.startsWith('/fantasy/calendar') && !currentPath.startsWith('/fantasy/rankings');
  const isTeamStartingWeek = (team && team.weekId && (team.weekId >= weekId)) || false;

  useEffect(() => {
    // If we don't have a weekId set yet
    if (weekId === 0) {
      const defaultWeekId = (isNewTeamRoute) ? competitionData.deadlineInfo.deadlineWeek : (isPointsRoute) ? Math.max(competitionData.deadlineInfo.deadlineWeek-1, 1) : 
        (isRootRoute && competitionData.deadlineInfo.displayWeek < competitionData.deadlineInfo.deadlineWeek) ? 
          competitionData.deadlineInfo.displayWeek 
          : competitionData.deadlineInfo.deadlineWeek;
      setWeekId(defaultWeekId);
      //todo test if needed updateForWeek(newWeekId);
    }
  }, [weekId, isPointsRoute, setWeekId, competitionData]);

  const currentWeekIndex = weeks.findIndex((week: Week) => week.weekId === weekId) || 0;
  const currentWeekData = competitionData.matches.filter((m: Match) => m.weekId === weekId);

  const goToPreviousWeek = () => {
    if (currentWeekIndex > 0) {
      const newWeekId = weeks[currentWeekIndex - 1]!.weekId;
      setWeekId(newWeekId);
      updateForWeek(newWeekId);

      // Handle navigation from edit to points
      if (isEditRoute && newWeekId < competitionData.deadlineInfo.deadlineWeek) { // && (team?.weekId || 0) < newWeekId) {
        navigate(`/fantasy/points/${params.id}`);
      }
    }
  };

  const goToNextWeek = () => {
    if (currentWeekIndex < weeks.length - 1) {
      const newWeekId = weeks[currentWeekIndex + 1]!.weekId;
      setWeekId(newWeekId);
      updateForWeek(newWeekId);

      // Handle navigation from points to edit
      if (isPointsRoute && newWeekId >= competitionData.deadlineInfo.deadlineWeek) {
        navigate(`/fantasy/edit/${params.id}`);
      }
    }
  };

  return (
    showWeekSwitch && <div className={`text-${brand} p-4 flex items-center justify-between`}>
      <button
        onClick={goToPreviousWeek}
        disabled={currentWeekIndex === 0 || isNewTeamRoute || isTeamStartingWeek}
        aria-label="Previous week"
      >
        <CircleChevronLeft
          fill={brand}
          color="#FFF"
          className={currentWeekIndex === 0 || isNewTeamRoute ? 'opacity-50' : ''}
        />
      </button>
      <h1 className={`text-${brand}-header font-extrabold font-extra-italic italic`}>
        {currentWeekData.length > 0
          ? `${currentWeekData[0].weekId}. ${currentWeekData.map((match: Match) => match.feedUrl).join(' - ')}, ${moment.utc(currentWeekData[0].date).local().format('dd DD/MM [om] H:mm')}`
          : 'No week selected'}
      </h1>
      <button
        onClick={goToNextWeek}
        disabled={currentWeekIndex === weeks.length - 1 || isNewTeamRoute || (isPublicRoute && currentWeekIndex+2 >= competitionData.deadlineInfo.deadlineWeek)}
        aria-label="Next week"
      >
        <CircleChevronRight
          fill={`text-${brand}`}
          color="#FFF"
          className={currentWeekIndex === weeks.length - 1 || isNewTeamRoute ? 'opacity-50' : ''}
        />
      </button>
    </div>
  );
};

// This component will redirect users with a teamId from /new to /edit/:teamId
const RedirectIfTeamExists = ({ teamId, weekId }: any) => {
  if (teamId) {
    return <Navigate to={`/fantasy/edit/${teamId}`} replace />;
  }
  return <NewTeam weekId={weekId} />;
};

const queryClient = new QueryClient();

// Component to pass the URL param id to TeamProvider
const PublicTeamRoute = ({ weekId, competitionData } : any) => {
  const { id } = useParams();

  return (
    <TeamProvider teamId={id} competitionData={competitionData} currentWeekId={weekId}>
      <Points weekId={weekId} />
    </TeamProvider>
  );
};

export default function App() {
  const [isClient, setIsClient] = useState(false);
  const [teamId, setTeamId] = useState<string | null>(null);
  const [user, setUser] = useState<{email?: string, name?: string}>({});
  // Read teamId from cookie on client side
  useEffect(() => {
    const getCookieValue = (name: string): string | null => {
      const value = `; ${document.cookie}`;
      const parts = value.split(`; ${name}=`);
      if (parts.length === 2) {
        const popped = parts.pop();
        return popped ? popped.split(';').shift() || null : null;
      }
      return null;
    };

    // Check for authentication
    //WONT WORK ON FRONTEND USER USERID
    // const dpgUit = getCookieValue('dpg-component-uit');
    // console.log('dpgUit', dpgUit);
    // if(dpgUit) {
    //   setUser(decodeJwt(dpgUit))
    // } else {
    //   console.log('no dpgUit found')
    // }

    const teamIdFromCookie = getCookieValue('teamId');
    setTeamId(teamIdFromCookie);
    console.log(teamIdFromCookie)
    console.log('teamIdFromCookie', teamIdFromCookie);

  }, []);

  const { competitionData, weekId } = useCompetitionContext();

  useEffect(() => {
    if (!user) return; // Wait for session data
    const interval = setInterval(() => {
      if (window.$crisp && typeof window.$crisp.push === 'function') {
        window.$crisp.push(["set", "user:email", [user?.email]]);
        window.$crisp.push(["set", "user:nickname", [user?.name]]);
        clearInterval(interval);
      }
    }, 100);

    return () => clearInterval(interval);
  }, [user]);

  useEffect(() => {
    setIsClient(true);

    // if (typeof window !== 'undefined') {
    //   require('iframe-resizer/js/iframeResizer.contentWindow');
    // }

    if (typeof window !== 'undefined') {
      require('@iframe-resizer/child');
      window.iframeResizer = {
        autoResize: true,
      };
      console.log('iframeResizer loaded', window.iframeResizer);
    }

  }, []);

  function LayoutWithWeekSwitch() {
    return (
      <>
        <WeekSwitch competitionData={competitionData} />
        <Outlet />
      </>
    );
  }

  return (
    <>
      {isClient && (
        <QueryClientProvider client={queryClient}>
          <BrowserRouter>
            <RouteGuard user={user} teamId={teamId}>
              <TeamProvider teamId={teamId} competitionData={competitionData} currentWeekId={weekId}>
                <Routes>
                  <Route path="/fantasy" element={<LayoutWithWeekSwitch />}>
                    <Route path="new" element={<RedirectIfTeamExists teamId={teamId} weekId={weekId} />} />
                    <Route path="edit/:id" element={<EditTeam teamId={teamId} weekId={weekId} />} />
                    <Route path="points/:id" element={weekId !== 0 ? <Points weekId={weekId} /> : <LoadingSpinner />} />
                    {/* Public route with special handling to use the URL param as teamId */}
                    <Route path="public/:id" element={
                      weekId !== 0 ? <PublicTeamRoute weekId={weekId} competitionData={competitionData} />
                      : <LoadingSpinner />
                    } />
                    <Route path="leagues" element={<Leagues weekId={weekId} />} />
                    {/* <Route path="calendar" element={<Calendar />} /> */}
                    <Route path="rankings" element={<Ranking />} />
                  </Route>
                  <Route path="/*" element={<div>404 Not Found</div>} />
                </Routes>
              </TeamProvider>
            </RouteGuard>
          </BrowserRouter>
        </QueryClientProvider>
      )}
    </>
  );
}